// STLGenerator.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import DownloadOverlay from './DownloadOverlay';

// --- three.js imports (browser rendering, no backend GPU) ---
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// --- limits for extrusions ---
const BG_MIN = 0.1;
const BG_MAX = 3.0;
const EX_MIN = 3.1; // non-background layers (UI: 10.1–20)
const EX_MAX = 6.0;
const EX_STEP = 0.1;

function clampNumber(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}
function normalizeStep(n) {
  const v = Number(n);
  return Number.isFinite(v) ? Number(v.toFixed(1)) : EX_MIN;
}

// --- helper to normalize color_table.json into { nameLower: '#rrggbb' } ---
function normalizeColorTable(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;

  const toHex = (r, g, b) => {
    const h = (v) =>
      Math.max(0, Math.min(255, Number(v) || 0))
        .toString(16)
        .padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
  };

  for (const [name, val] of Object.entries(raw)) {
    const key = String(name).trim().toLowerCase();
    let hex = null;

    if (typeof val === 'string') {
      hex = val;
    } else if (Array.isArray(val) && val.length >= 3) {
      hex = toHex(val[0], val[1], val[2]);
    } else if (val && typeof val === 'object') {
      const r = val.r ?? val.R ?? val.red;
      const g = val.g ?? val.G ?? val.green;
      const b = val.b ?? val.B ?? val.blue;
      if ([r, g, b].every((v) => Number.isFinite(Number(v)))) {
        hex = toHex(r, g, b);
      }
    }

    if (hex) out[key] = hex;
  }
  return out;
}

export default function STLGenerator({
  filename,
  email,
  credits,
  setCredits,
  isProcessed,
  processedImage,
  overlayImage,
  colorList,
  resetTrigger,
  onResetComplete,
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [progress, setProgress] = useState(0);

  const [statusNote, setStatusNote] = useState('');

  const [showOverlay, setShowOverlay] = useState(false);
  const [pendingDownloadType, setPendingDownloadType] = useState(null);

  const [extrusionValues, setExtrusionValues] = useState([]);
  const [makeItFlat, setMakeItFlat] = useState(true);
  const [extrusionsLocked, setExtrusionsLocked] = useState(false);

  const [colorMap, setColorMap] = useState({});
  const [alreadyDownloaded, setAlreadyDownloaded] = useState(false);

  // Local effective color list (uses prop if available, otherwise hydrates from metadata.json)
  const [effectiveColors, setEffectiveColors] = useState([]);

  const [showExtrusionOverlayPanel, setShowExtrusionOverlayPanel] =
    useState(false);

  const [showTopDownloadButton, setShowTopDownloadButton] = useState(false);

  const prevShowTopDownloadRef = useRef(false);

  const viewerRendererRef = useRef(null);
  const captureApiRef = useRef(null); // { forceRenderAndGetCanvas, resetToInitialView, awaitStableInitialView, waitUntilModelsReady }
  const viewerSectionRef = useRef(null); // used to scroll into view when top button appears

  const [pendingAutoSnapshot, setPendingAutoSnapshot] = useState(false);
  const [snapshotDone, setSnapshotDone] = useState(false);
  const snapshotDoneRef = useRef(false);

  const topDownloadButtonRef = useRef(null);
  const [hasTriggeredScroll, setHasTriggeredScroll] = useState(false);

  // --- One-shot auto-open for the single overlay (no auto-close) -------------
  const autoOpenExtrusionOnceRef = useRef(false);

  useEffect(() => {
    if (
      !prevShowTopDownloadRef.current && // rising edge: false -> true
      showTopDownloadButton &&
      viewerSectionRef.current &&
      typeof viewerSectionRef.current.scrollIntoView === 'function'
    ) {
      viewerSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
    prevShowTopDownloadRef.current = showTopDownloadButton;
  }, [showTopDownloadButton]);

  const username = useMemo(
    () => (email || '').split('@')[0] || '',
    [email]
  );

  const stepExtrusion = useCallback(
    (index, delta) => {
      setExtrusionValues((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const next = [...prev];

        const [min, max] = index === 0 ? [BG_MIN, BG_MAX] : [EX_MIN, EX_MAX];
        const currentRaw = next[index];
        const current = Number.isFinite(Number(currentRaw))
          ? Number(currentRaw)
          : index === 0
          ? 3
          : 6;

        let newVal = current + delta * EX_STEP;
        newVal = normalizeStep(clampNumber(newVal, min, max));

        if (index > 0 && makeItFlat) {
          for (let j = 1; j < next.length; j++) {
            next[j] = newVal;
          }
        } else {
          next[index] = newVal;
        }
        return next;
      });
    },
    [makeItFlat]
  );

  const projectRoot = useMemo(() => {
    const pickRoot = (u) => {
      if (!u) return '';
      const m = String(u).match(/(\/outputs\/[^/]+\/output-[^/?]+)/);
      return m ? m[1] : '';
    };
    const fromProcessed = pickRoot(processedImage);
    const fromOverlay = pickRoot(overlayImage);
    if (fromProcessed) return fromProcessed;
    if (fromOverlay) return fromOverlay;

    const baseStr = String(filename || '').trim();
    if (!username || !baseStr) return '';
    const parts = baseStr.split('.');
    const baseNoExt = parts.length > 1 ? parts.slice(0, -1).join('.') : baseStr;
    return `/outputs/${username}/output-${baseNoExt}`;
  }, [processedImage, overlayImage, filename, username]);

  // hydrate effectiveColors from prop or metadata.json
  useEffect(() => {
    // If parent passed colors, trust them
    if (Array.isArray(colorList) && colorList.length > 0) {
      setEffectiveColors(colorList.filter(Boolean));
      return;
    }

    // Otherwise, try to hydrate from metadata.json at projectRoot
    if (!projectRoot) return;

    (async () => {
      try {
        const res = await fetch(
          `${projectRoot}/metadata.json?t=${Date.now()}`,
          {
            cache: 'no-store',
          }
        );
        if (!res.ok) return;
        const meta = await res.json();

        const normPath = (p) =>
          String(p || '')
            .replace(/^\.\//, '')
            .replace(/\/+/g, '/')
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        const baseSvgRel = String(meta?.base_svg || '')
          .replace(/^\.\//, '')
          .trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== 'base')
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) =>
                e.file ? pickColorName(e.file) : String(e.name || '')
              )
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find(
            (c) => String(c?.name).toLowerCase() === 'base'
          );
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const parsed = [base, ...rest].filter(Boolean);

        if (parsed.length >= 1) {
          setEffectiveColors(parsed);
        }
      } catch (e) {
        console.warn('[STLGenerator] metadata color hydrate failed', e);
      }
    })();
  }, [colorList, projectRoot]);

  const colorsForUi = useMemo(
    () =>
      Array.isArray(effectiveColors) && effectiveColors.length > 0
        ? effectiveColors
        : Array.isArray(colorList) && colorList.length > 0
        ? colorList
        : [],
    [effectiveColors, colorList]
  );

  const nameNoExt = useMemo(() => {
    const s = String(filename || '').trim();
    if (!s) return '';
    const parts = s.split('.');
    return parts.length > 1 ? parts.slice(0, -1).join('') : s;
  }, [filename]);

  const [zeroCreditsGrace, setZeroCreditsGrace] = useState(false);
  const prevCreditsRef = useRef(credits);

  const [awaitingAuthorization, setAwaitingAuthorization] = useState(false);

  //detect the transition from >0 -> 0 credits (grants session-only grace)
  useEffect(() => {
    if (prevCreditsRef.current > 0 && credits === 0) {
      setZeroCreditsGrace(true);
    }
    prevCreditsRef.current = credits;
  }, [credits]);

  // --- derived flag: button is fully ready to download (text should flash) ---
  const downloadReady = useMemo(
    () =>
      showTopDownloadButton &&
      isDownloadEnabled &&
      snapshotDone &&
      !isDownloaded &&
      !(credits <= 0 && !zeroCreditsGrace),
    [
      showTopDownloadButton,
      isDownloadEnabled,
      snapshotDone,
      isDownloaded,
      credits,
      zeroCreditsGrace,
    ]
  );

  // load color table
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/color_table.json', { cache: 'no-cache' });
        if (!res.ok) throw new Error('Missing /color_table.json');
        const data = await res.json();
        if (!cancelled) setColorMap(normalizeColorTable(data));
      } catch {
        try {
          const res2 = await fetch('/color-table/', { cache: 'no-cache' });
          if (res2.ok && !cancelled) {
            const data2 = await res2.json();
            setColorMap(normalizeColorTable(data2));
          }
        } catch {}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- USER-LEVEL LEDGER: read helper (kept); write helper removed -----------
  const fetchDownloadedFlag = useCallback(async () => {
    const userLevelUrl = username
      ? `/outputs/${username}/downloaded.json?t=${Date.now()}`
      : null;

    const parseFlag = (data) => {
      if (typeof data === 'boolean') return data;
      if (data && typeof data === 'object') {
        if (typeof data.downloaded === 'boolean') return data.downloaded;
        if (nameNoExt in data) {
          const v = data[nameNoExt];
          return typeof v === 'boolean' ? v : !!v?.downloaded;
        }
      }
      return false;
    };

    let flag = false;
    try {
      if (userLevelUrl) {
        const r = await fetch(userLevelUrl, { cache: 'no-store' });
        if (r.ok) {
          flag = parseFlag(await r.json());
        } else if (r.status === 404) {
          flag = false; // ledger not created yet
        }
      }
    } catch {}
    setAlreadyDownloaded(flag);
    return flag;
  }, [username, nameNoExt]);

  useEffect(() => {
    fetchDownloadedFlag();
  }, [fetchDownloadedFlag]);

  // Reset flow
  useEffect(() => {
    if (resetTrigger) {
      setTimeout(() => {
        setIsGenerating(false);
        setIsDownloaded(false);
        setIsDownloadEnabled(false);
        setHasGenerated(false);
        setProgress(0);
        setStatusNote('');
        setExtrusionsLocked(false);
        setShowOverlay(false);
        setPendingDownloadType(null);
        setExtrusionValues([]);
        setMakeItFlat(true);
        setPendingAutoSnapshot(false);
        setSnapshotDone(false);
        setShowTopDownloadButton(false);
        snapshotDoneRef.current = false;
        onResetComplete();
        fetchDownloadedFlag();
        setAwaitingAuthorization(false);
        setHasTriggeredScroll(false);
        autoOpenExtrusionOnceRef.current = false;
      }, 0);
    }
  }, [resetTrigger, onResetComplete, fetchDownloadedFlag]);

  useEffect(() => {
    const resetHandler = () => {
      setIsDownloaded(false);
      setHasGenerated(false);
      setIsDownloadEnabled(false);
      setHasTriggeredScroll(false);
    };
    window.addEventListener('resetSTLDownloads', resetHandler);
    return () => window.removeEventListener('resetSTLDownloads', resetHandler);
  }, []);

  useEffect(() => {
    if (isProcessed) {
      fetch(`/progress/${username}?reset=true`).catch(() => {});
      setHasGenerated(false);
      setIsDownloaded(false);
      setIsDownloadEnabled(false);
      setProgress(0);
      setStatusNote('');
      setExtrusionsLocked(false);
      setShowOverlay(false);
      setPendingDownloadType(null);
      setPendingAutoSnapshot(false);
      setShowTopDownloadButton(false);
      setSnapshotDone(false);
      setAwaitingAuthorization(false);
      snapshotDoneRef.current = false;
      setHasTriggeredScroll(false);
      setMakeItFlat(true);
      autoOpenExtrusionOnceRef.current = false;
    }
  }, [isProcessed, username]);

  // Progress polling
  useEffect(() => {
    let interval;
    if (isGenerating) {
      interval = setInterval(async () => {
        try {
          const res = await fetch(`/progress/${username}`);
          const data = await res.json();
          if (data.progress !== undefined) setProgress(data.progress);
        } catch {}
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isGenerating, username]);

// Load extrusions from metadata.json instead of defaults
useEffect(() => {
  if (!Array.isArray(colorsForUi) || colorsForUi.length === 0) {
    setExtrusionValues([]);
    return;
  }
  if (!projectRoot) return;

  let cancelled = false;

  const loadExtrusions = async () => {
    try {
      const res = await fetch(`${projectRoot}/metadata.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!res.ok) {
        // fallback to old defaults if metadata missing
        const fallback = [3, ...Array(colorsForUi.length - 1).fill(4)];
        if (!cancelled) setExtrusionValues(fallback);
        return;
      }

      const meta = await res.json();
      const colorEntries = Array.isArray(meta.colors) ? meta.colors : [];

      // Build maps
      const nameToExtrusion = {};
      let baseExtrusion = null;

      for (const entry of colorEntries) {
        if (!entry || typeof entry !== 'object') continue;
        const idx = entry.index;
        const nm = entry.name;
        const ext = entry.extrusion;

        if (idx === 1 && typeof ext === 'number') {
          baseExtrusion = ext;
        }
        if (typeof nm === 'string' && typeof ext === 'number') {
          nameToExtrusion[nm.trim()] = ext;
        }
      }

      // Build extrusion array
      const list = [];

      // Background
      const bgName = colorsForUi[0];
      let bgExt = nameToExtrusion[bgName] ?? baseExtrusion ?? 10;
      bgExt = normalizeStep(clampNumber(bgExt, BG_MIN, BG_MAX));
      list.push(bgExt);

      // others
      for (let i = 1; i < colorsForUi.length; i++) {
        const nm = colorsForUi[i];
        let ext = nameToExtrusion[nm];
        if (typeof ext !== 'number') ext = 4; // fallback for missing
        ext = normalizeStep(clampNumber(ext, EX_MIN, EX_MAX));
        list.push(ext);
      }

      if (!cancelled) {
        setExtrusionValues(list);
      }
    } catch (e) {
      console.warn('Failed loading extrusions from metadata:', e);
      const fallback = [3, ...Array(colorsForUi.length - 1).fill(4)];
      if (!cancelled) setExtrusionValues(fallback);
    }
  };

  loadExtrusions();
  return () => {
    cancelled = true;
  };
}, [colorsForUi, projectRoot]);


  // Make it flat
  useEffect(() => {
    if (!makeItFlat || extrusionValues.length <= 1) return;
    const clamped = extrusionValues.map((v, i) =>
      i === 0 ? clampNumber(v, BG_MIN, BG_MAX) : clampNumber(v, EX_MIN, EX_MAX)
    );
    const target = Math.max(...clamped.slice(1));
    let changed = false;
    const next = clamped.map((v, i) => {
      if (i === 0) return v;
      if (v !== target) {
        changed = true;
        return target;
      }
      return v;
    });
    if (changed) setExtrusionValues(next);
  }, [makeItFlat, extrusionValues]);

  // Auto-open the single overlay (Extrusion Settings) once per processing session.
  // No auto-close; user decides.
  useEffect(() => {
    if (!isProcessed) return;

    const colorsReady = Array.isArray(colorsForUi) && colorsForUi.length > 0;
    const extrusionsReady =
      Array.isArray(extrusionValues) &&
      extrusionValues.length === colorsForUi.length &&
      extrusionValues.every((v) => v !== undefined);

    if (!colorsReady || !extrusionsReady) return;

    if (!autoOpenExtrusionOnceRef.current) {
      autoOpenExtrusionOnceRef.current = true;
      setShowExtrusionOverlayPanel(true);
    }
  }, [isProcessed, colorsForUi, extrusionValues]);

  // Inputs
  const handleExtrusionChange = useCallback(
    (index, raw) => {
      if (raw === '') {
        setExtrusionValues((prev) => {
          const next = [...prev];
          next[index] = '';
          return next;
        });
        return;
      }
      let v = typeof raw === 'string' ? raw.replace(',', '.') : raw;
      const num = parseFloat(v);
      if (Number.isNaN(num)) return;
      const [min, max] = index === 0 ? [BG_MIN, BG_MAX] : [EX_MIN, EX_MAX];
      const clamped = clampNumber(num, min, max);
      setExtrusionValues((prev) => {
        const next = [...prev];
        if (index > 0 && makeItFlat) {
          for (let j = 1; j < next.length; j++) next[j] = clamped;
        } else {
          next[index] = clamped;
        }
        return next;
      });
    },
    [makeItFlat]
  );

  const handleExtrusionBlur = useCallback((index) => {
    setExtrusionValues((prev) => {
      const next = [...prev];
      const [min, max] = index === 0 ? [BG_MIN, BG_MAX] : [EX_MIN, EX_MAX];
      const normalized = normalizeStep(
        clampNumber(parseFloat(next[index]), min, max)
      );
      next[index] = normalized;
      return next;
    });
  }, []);

  // Snapshot helpers
  const waitRaf = () =>
    new Promise((resolve) => requestAnimationFrame(resolve));

  const captureAndUploadThumbnailOnDemand = useCallback(async () => {
    const api = captureApiRef.current;
    if (!api || !api.forceRenderAndGetCanvas) return false;

    const getCanvas = async () => {
      const canvas = await api.forceRenderAndGetCanvas();
      return canvas;
    };

    const tryOnce = async () => {
      const canvas = await getCanvas();
      if (!canvas || canvas.width === 0 || canvas.height === 0) return false;

      await waitRaf();

      return new Promise((resolve) => {
        canvas.toBlob(async (blob) => {
          if (!blob || blob.size < 2048) return resolve(false);
          const form = new FormData();
          form.append('file', blob, 'thumbnail.png');
          form.append('username', username);
          form.append('project', nameNoExt);
          try {
            const resp = await fetch('/upload-thumbnail/', {
              method: 'POST',
              body: form,
            });
            resolve(resp.ok);
          } catch {
            resolve(false);
          }
        }, 'image/png');
      });
    };

    for (let i = 0; i < 4; i++) {
      const ok = await tryOnce();
      if (ok) return true;
      await waitRaf();
    }
    return false;
  }, [username, nameNoExt]);

  const uploadThumbnailFromRenderer = useCallback(async () => {
    try {
      const renderer = viewerRendererRef.current;
      const canvas = renderer?.domElement;
      if (!canvas || canvas.width === 0 || canvas.height === 0) return;

      const gl = renderer.getContext?.();
      renderer.render?.(renderer.__scene || null, renderer.__camera || null);
      if (gl && typeof gl.finish === 'function') {
        try {
          gl.finish();
        } catch {}
      }

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png')
      );
      if (!blob || blob.size < 1024) return;

      const form = new FormData();
      form.append('file', blob, 'thumbnail.png');
      form.append('username', username);
      form.append('project', nameNoExt);

      await fetch('/upload-thumbnail/', { method: 'POST', body: form });
    } catch (e) {
      console.error('thumbnail upload failed', e);
    }
  }, [username, nameNoExt]);

  const setProgressFile = useCallback(
    async (value = 100) => {
      try {
        await fetch(`/progress/${username}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ progress: value, project: nameNoExt }),
        });
      } catch (e) {
        console.warn('Failed to set progress.json (non-fatal):', e);
      }
    },
    [username, nameNoExt]
  );

  // GENERATE call
  const callGenerateEndpoint = async (cleanExtrusions) => {
    const response = await fetch('/generate-stl-coaster/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        email,
        colors: colorsForUi,
        extrusions: cleanExtrusions,
        makeItFlat,
      }),
    });
    return response;
  };

  // generation path WITHOUT charging
  const startGenerationNoCharge = async () => {
    if (!filename || !email) return;
    try {
      setStatusNote('3D rendering of the design will appear below:');

      setIsGenerating(true);
      setIsDownloaded(false);
      setIsDownloadEnabled(false);
      setHasGenerated(false);
      setProgress(0);

      const cleanExtrusions = extrusionValues.map((v, i) => {
        const [min, max] = i === 0 ? [BG_MIN, BG_MAX] : [EX_MIN, EX_MAX];
        return normalizeStep(clampNumber(v, min, max));
      });

      const response = await callGenerateEndpoint(cleanExtrusions);

      if (response && response.ok) {
        setHasGenerated(true);
        setIsDownloadEnabled(true);
        setProgress(100);
        setProgressFile(100);
        if (!snapshotDoneRef.current) {
          setPendingAutoSnapshot(true);
          setSnapshotDone(false);
        }
      } else {
        console.error(
          'STL generation failed:',
          response ? await response.text() : 'no response'
        );
      }
    } catch (error) {
      console.error('Error during generation (no charge):', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // 5) OVERLAY CONFIRM — proceed; awaitingAuthorization no longer needed
  const handleConfirmAuthorizeAndGenerate = async () => {
    setShowOverlay(false);
    setPendingDownloadType(null);
    setAwaitingAuthorization(false); // generation will set isGenerating=true
    if (!filename || !email) return;

    if (!statusNote)
      setStatusNote('3D rendering of the design will appear below:');

    try {
      const deduct = await fetch('/deduct-credit/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!deduct.ok) {
        console.error('Credit deduction failed:', await deduct.text());
        setExtrusionsLocked(false);
        return;
      }
      const updated = await deduct.json();
      if (updated && typeof updated.credits === 'number') {
        setCredits(updated.credits);
      }

      setIsGenerating(true);
      setIsDownloaded(false);
      setIsDownloadEnabled(false);
      setHasGenerated(false);
      setProgress(0);

      const cleanExtrusions = extrusionValues.map((v, i) => {
        const [min, max] = i === 0 ? [BG_MIN, BG_MAX] : [EX_MIN, EX_MAX];
        return normalizeStep(clampNumber(v, min, max));
      });

      const response = await callGenerateEndpoint(cleanExtrusions);

      if (response && response.ok) {
        setHasGenerated(true);
        setIsDownloadEnabled(true);
        setProgress(100);
        setProgressFile(100);
        if (!snapshotDoneRef.current) {
          setPendingAutoSnapshot(true);
          setSnapshotDone(false);
        }
      } else {
        console.error(
          'STL generation failed:',
          response ? await response.text() : 'no response'
        );
        setExtrusionsLocked(false);
      }
    } catch (error) {
      console.error('Error during authorize-and-generate flow:', error);
      setExtrusionsLocked(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateClick = async () => {
    if (!filename || hasGenerated) return;

    // scroll down towards the viewer section as soon as user clicks Generate
    if (
      viewerSectionRef.current &&
      typeof viewerSectionRef.current.scrollIntoView === 'function'
    ) {
      viewerSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    setExtrusionsLocked(true);
    setStatusNote('3D rendering of the design will appear below:');
    setProgress(0);

    // show the inline render-status / download button above the viewer
    setShowTopDownloadButton(true);

    // lock button immediately while we check the ledger
    setAwaitingAuthorization(true);

    const flag = await fetchDownloadedFlag();
    if (flag) {
      // no overlay path → unlock this temporary lock (isGenerating will lock next)
      setAwaitingAuthorization(false);
      startGenerationNoCharge();
      return;
    }

    setPendingDownloadType('authorize');
    setShowOverlay(true); // keep awaitingAuthorization = true while overlay is open
  };

  // Download (no extra deduct)
  const initiateDownload = async () => {
    setStatusNote('Download may take a few seconds to initiate.');
    try {
      setIsDownloaded(true);
      setIsDownloadEnabled(false);

      const response = await fetch('/download/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/zip',
        },
        body: JSON.stringify({ name: nameNoExt, email }),
      });

      if (!response.ok) {
        console.error('Download failed:', await response.text());
        setIsDownloaded(false);
        setIsDownloadEnabled(true);
        return;
      }

      const blob = await new Promise((resolve) =>
        response.blob().then((b) => resolve(b))
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${nameNoExt}_grouped.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading STL:', error);
      setIsDownloaded(false);
      setIsDownloadEnabled(true);
    } finally {
      // note persists
    }
  };

  // Cancel any pending auto-snapshot on download (prevents late recentering)
  const handleDownloadClick = async () => {
    setPendingAutoSnapshot(false);
    if (
      !isDownloadEnabled ||
      isDownloaded ||
      (credits <= 0 && !zeroCreditsGrace)
    )
      return;
    setStatusNote('Download may take a few seconds to initiate.');
    await initiateDownload();
  };

  // 4) OVERLAY CLOSE — ungrey the button when user clicks [x]
  const handleOverlayClose = () => {
    setShowOverlay(false);
    setPendingDownloadType(null);
    setAwaitingAuthorization(false); // <-- re-enable the button
    if (!isGenerating && !hasGenerated) setExtrusionsLocked(false);
  };

  const nameToHex = useCallback(
    (name) => {
      if (!name) return null;
      const key = String(name).trim().toLowerCase();
      return colorMap[key] || null;
    },
    [colorMap]
  );

  const emsPartUrls = useMemo(() => {
    if (!projectRoot || !Array.isArray(colorsForUi) || colorsForUi.length === 0)
      return [];
    return colorsForUi.map((c, idx) => {
      const index = idx + 1;
      return `${projectRoot}/stls/EMSparts/${index} - ${c}.stl`;
    });
  }, [projectRoot, colorsForUi]);

  const emsPartColors = useMemo(() => {
    if (!Array.isArray(colorsForUi)) return [];
    return colorsForUi.map((c) => nameToHex(c) || null);
  }, [colorsForUi, nameToHex]);

  // --- Inline STL Viewer (Three.js) ------------------------------------------
  const Viewer = ({ urls, colors, onReady, onReadyCaptureApi, locked }) => {
    const mountRef = useRef(null);
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const meshesRef = useRef([]);
    const groupRef = useRef(null);
    const notifiedReadyRef = useRef(false);

    const initialPoseRef = useRef({
      camPos: new THREE.Vector3(0, 0, 150),
      camQuat: new THREE.Quaternion(),
      target: new THREE.Vector3(0, 0, 0),
      near: 0.1,
      far: 5000,
    });

    // models-ready gate
    const modelsReadyRef = useRef(false);
    const modelsReadyResolversRef = useRef([]);
    const signalModelsReady = () => {
      modelsReadyRef.current = true;
      const resolvers = modelsReadyResolversRef.current.splice(0);
      resolvers.forEach((r) => r());
    };
    const waitUntilModelsReady = async () => {
      if (modelsReadyRef.current) return;
      await new Promise((resolve) =>
        modelsReadyResolversRef.current.push(resolve)
      );
    };

    useEffect(() => {
      const mount = mountRef.current;
      if (!mount) return;

      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true,
        });
      } catch (e) {
        console.error('WebGL renderer init failed:', e);
        return;
      }
      renderer.sortObjects = true;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      renderer.setClearColor(0x000000, 0);
      mount.appendChild(renderer.domElement);
      rendererRef.current = renderer;

      const scene = new THREE.Scene();
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(
        45,
        mount.clientWidth / mount.clientHeight,
        0.1,
        5000
      );
      camera.position.set(0, 0, 150);
      cameraRef.current = camera;

      // Lights
      scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      const key = new THREE.DirectionalLight(0xffffff, 0.9);
      key.position.set(1, 1, 2);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xffffff, 0.6);
      fill.position.set(-1, -0.5, -1.5);
      scene.add(fill);

      // Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.enabled = !locked; // initial lock state
      controlsRef.current = controls;

      // Resize
      const syncSize = () => {
        if (!mount || !rendererRef.current || !cameraRef.current) return;
        const { clientWidth, clientHeight } = mount;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const needResize =
          rendererRef.current.domElement.width !==
            Math.floor(clientWidth * dpr) ||
          rendererRef.current.domElement.height !==
            Math.floor(clientHeight * dpr);
        if (needResize) {
          rendererRef.current.setSize(clientWidth, clientHeight);
        }
        cameraRef.current.aspect = clientWidth / clientHeight || 1;
        cameraRef.current.updateProjectionMatrix();
      };
      const onResize = () => syncSize();
      window.addEventListener('resize', onResize);

      // Load STLs
      const loader = new STLLoader();
      loader.setCrossOrigin('anonymous');

      const group = new THREE.Group();
      groupRef.current = group;
      scene.add(group);
      meshesRef.current = [];

      const loadOne = (url, idx) =>
        new Promise((resolve) => {
          loader.load(
            url,
            (geo) => {
              geo.computeVertexNormals();
              const colorHex =
                colors?.[idx] ??
                new THREE.Color()
                  .setHSL(((idx * 47) % 360) / 360, 0.55, 0.6)
                  .getHex();
              const mat = new THREE.MeshStandardMaterial({
                color: colorHex,
                metalness: 0.05,
                roughness: 0.9,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: idx + 1,
              });
              const mesh = new THREE.Mesh(geo, mat);
              mesh.position.z += 0.02 * idx;
              mesh.renderOrder = idx;

              group.add(mesh);
              meshesRef.current.push(mesh);
              resolve(true);
            },
            undefined,
            (err) => {
              console.warn(
                'Missing or failed STL:',
                url,
                err?.message || err
              );
              resolve(false);
            }
          );
        });

      (async () => {
        const results = await Promise.all(urls.map((u, i) => loadOne(u, i)));
        const anyLoaded = results.some(Boolean);

        if (anyLoaded) {
          // fit & camera
          const bbox = new THREE.Box3().setFromObject(group);
          const sphere = bbox.getBoundingSphere(new THREE.Sphere());
          const radius = sphere.radius || 1;

          const fitOffset = 1.02;
          const fov = THREE.MathUtils.degToRad(camera.fov);
          const dist = (radius / Math.sin(fov / 2)) * fitOffset;

          const tilt = THREE.MathUtils.degToRad(25);
          camera.position.set(
            sphere.center.x + dist * Math.sin(tilt),
            sphere.center.y + dist * Math.sin(tilt),
            sphere.center.z + dist * Math.cos(tilt)
          );
          camera.near = Math.max(0.1, radius / 100);
          camera.far = Math.max(5000, radius * 20);
          camera.lookAt(sphere.center);
          camera.updateProjectionMatrix();

          controls.target.copy(sphere.center);
          controls.update();

          controls.minDistance = dist * 0.25;
          controls.maxDistance = dist * 4;

          // save initial pose
          const initialPose = {
            camPos: camera.position.clone(),
            camQuat: camera.quaternion.clone(),
            target: controls.target.clone(),
            near: camera.near,
            far: camera.far,
          };
          initialPoseRef.current = initialPose;

          controls.saveState();

          // pre-warm one render to stabilize first frame
          renderer.render(scene, camera);

          // mark models ready AFTER initial pose is set & pre-rendered
          signalModelsReady();

          if (!notifiedReadyRef.current) {
            notifiedReadyRef.current = true;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => onReady?.(renderer));
            });
          }
        } else {
          // Even if none loaded, still resolve; parent may show 2D preview instead
          signalModelsReady();
          if (!notifiedReadyRef.current) {
            notifiedReadyRef.current = true;
            onReady?.(renderer);
          }
        }
      })();

      // RAF
      let frameId = 0;
      const animate = () => {
        frameId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // capture/reset API
      const gl = renderer.getContext();

      const forceRenderAndGetCanvas = async () => {
        const mountEl = mountRef.current;
        if (mountEl && renderer && camera) {
          const { clientWidth, clientHeight } = mountEl;
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          const needResize =
            renderer.domElement.width !==
              Math.floor(clientWidth * dpr) ||
            renderer.domElement.height !==
              Math.floor(clientHeight * dpr);
          if (needResize) {
            renderer.setSize(clientWidth, clientHeight);
            camera.aspect = (clientWidth || 1) / (clientHeight || 1);
            camera.updateProjectionMatrix();
          }
        }

        controls.update();
        renderer.render(scene, camera);
        if (gl && typeof gl.finish === 'function') {
          try {
            gl.finish();
          } catch {}
        }
        await new Promise((resolve) => requestAnimationFrame(resolve));
        return renderer.domElement;
      };

      const resetToInitialView = async () => {
        const mountEl = mountRef.current;
        if (mountEl) {
          const { clientWidth, clientHeight } = mountEl;
          renderer.setSize(clientWidth, clientHeight);
          camera.aspect = (clientWidth || 1) / (clientHeight || 1);
          camera.updateProjectionMatrix();
        }

        controls.reset();

        const init = initialPoseRef.current;
        if (init) {
          camera.position.copy(init.camPos);
          camera.quaternion.copy(init.camQuat);
          camera.near = init.near;
          camera.far = init.far;
          camera.updateProjectionMatrix();
          controls.target.copy(init.target);
        }

        controls.update();
        await new Promise((resolve) => requestAnimationFrame(resolve));
        renderer.render(scene, camera);
        if (gl && typeof gl.finish === 'function') {
          try {
            gl.finish();
          } catch {}
        }
        await new Promise((resolve) => requestAnimationFrame(resolve));
      };

      // --- Stability helpers -------------------------------------------------
      const waitFrames = (n = 1) =>
        new Promise((resolve) => {
          let i = 0;
          const step = () => {
            i += 1;
            if (i >= n) resolve();
            else requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });

      // Wait until the initial (centered) view is rendered and the GPU is flushed.
      const awaitStableInitialView = async () => {
        await waitFrames(2);
        controls.update();
        renderer.render(scene, camera);
        if (gl && typeof gl.finish === 'function') {
          try {
            gl.finish();
          } catch {}
        }
        await waitFrames(1);
      };

      if (typeof onReadyCaptureApi === 'function') {
        onReadyCaptureApi({
          forceRenderAndGetCanvas,
          resetToInitialView,
          awaitStableInitialView,
          waitUntilModelsReady,
        });
      }

      // Cleanup
      return () => {
        cancelAnimationFrame(frameId);
        window.removeEventListener('resize', onResize);
        controls.dispose();
        renderer.dispose();
        meshesRef.current.forEach((m) => {
          m.geometry?.dispose?.();
          if (Array.isArray(m.material))
            m.material.forEach((mm) => mm?.dispose?.());
          else m.material?.dispose?.();
        });
        scene.clear();
        if (
          renderer.domElement &&
          renderer.domElement.parentNode === mount
        ) {
          mount.removeChild(renderer.domElement);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urls.join('|'), colors.join('|')]);

    // React to lock state changes (enable/disable controls + pointer events)
    useEffect(() => {
      const controls = controlsRef.current;
      const renderer = rendererRef.current;
      if (controls) controls.enabled = !locked;
      if (renderer?.domElement) {
        renderer.domElement.style.pointerEvents = locked ? 'none' : 'auto';
      }
    }, [locked]);

    return <div ref={mountRef} style={{ width: '100%', height: '100%' }} />;
  };

  // AUTO SNAPSHOT EFFECT (viewer-driven & model-ready gated)
  useEffect(() => {
    let cancelled = false;

    const maybeDoSnapshot = async () => {
      if (!pendingAutoSnapshot) return;
      if (!hasGenerated) return;
      if (snapshotDoneRef.current) {
        setPendingAutoSnapshot(false);
        return;
      }

      const api = captureApiRef.current;
      if (
        !api ||
        !api.resetToInitialView ||
        !api.forceRenderAndGetCanvas ||
        !api.awaitStableInitialView ||
        !api.waitUntilModelsReady
      ) {
        // try again on next tick if API not ready yet
        requestAnimationFrame(() =>
          setPendingAutoSnapshot((v) => v)
        ); // nudge effect to run again
        return;
      }

      try {
        // ensure meshes are loaded and initial pose was saved
        await api.waitUntilModelsReady();
        if (cancelled) return;

        // reset, wait for stable centered render, then capture
        await api.resetToInitialView();
        await api.awaitStableInitialView();
        if (cancelled) return;

        const ok = await captureAndUploadThumbnailOnDemand();
        if (!ok) {
          await new Promise((r) => requestAnimationFrame(r));
          if (!cancelled) await captureAndUploadThumbnailOnDemand();
        }
      } finally {
        if (!cancelled) {
          snapshotDoneRef.current = true;
          setSnapshotDone(true); // unlock UI
          setPendingAutoSnapshot(false);
        }
      }
    };

    maybeDoSnapshot();
    return () => {
      cancelled = true;
    };
  }, [pendingAutoSnapshot, hasGenerated, captureAndUploadThumbnailOnDemand]);

  // AUTO-SCROLL when Download button switches to "Download Zipped 3D Files"
  useEffect(() => {
    const readyForDownload =
      showTopDownloadButton &&
      isDownloadEnabled &&
      snapshotDone &&
      !isDownloaded;

    if (readyForDownload && !hasTriggeredScroll) {
      const el = topDownloadButtonRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        setHasTriggeredScroll(true);
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // reset flag when not ready, so next generation can scroll again
    if (!readyForDownload && hasTriggeredScroll) {
      setHasTriggeredScroll(false);
    }
  }, [
    showTopDownloadButton,
    isDownloadEnabled,
    snapshotDone,
    isDownloaded,
    hasTriggeredScroll,
  ]);

  const controlsLocked = extrusionsLocked || isGenerating || hasGenerated;

  return (
    <>
      <DownloadOverlay
        visible={showOverlay}
        onClose={handleOverlayClose}
        onConfirm={handleConfirmAuthorizeAndGenerate}
        username={username}
        project={nameNoExt}
        credits={1}                     // ⭐ NEW — STLGenerator uses 1 credit
        extra={{ source: "overlay" }}
      />

      {/* OUTER WRAPPER */}
      <div
        style={{
          display: isProcessed ? 'flex' : 'none',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 0,
          borderRadius: '12px',
          flexWrap: 'wrap',
          height: 'auto',
          width: '100%',
          position: 'relative',
          paddingTop: 0,
          marginTop: 0,
        }}
      >
        {isProcessed && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '3vw',
              marginTop: 0,
              flexWrap: 'wrap',
            }}
          >
            {/* Center: Status + Viewer */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '81vmin',
                maxWidth: '100%',
              }}
            >
              {/* TOP: Generate button (Save Project & Create 3D File) */}
              <div
                style={{
                  marginBottom: '0vh',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <button
                  onClick={handleGenerateClick}
                  disabled={
                    !isProcessed ||
                    isGenerating ||
                    hasGenerated ||
                    awaitingAuthorization ||
                    showOverlay ||
                    ((credits <= 0 && !zeroCreditsGrace) &&
                      !alreadyDownloaded)
                  }
                  style={{
                    width: '81vmin',
                    padding: '0.8vmin 0',
                    background: '#93c5fd',
                    border: '0px solid #94a3b8',
                    borderRadius: '10px',
                    fontSize: '1.1vw',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    cursor:
                      !isProcessed ||
                      isGenerating ||
                      hasGenerated ||
                      awaitingAuthorization ||
                      showOverlay ||
                      ((credits <= 0 && !zeroCreditsGrace) &&
                        !alreadyDownloaded)
                        ? 'not-allowed'
                        : 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  title={
                    awaitingAuthorization || showOverlay
                      ? 'Awaiting your confirmation…'
                      : (credits <= 0 && !zeroCreditsGrace) &&
                        !alreadyDownloaded
                      ? 'No credits available'
                      : alreadyDownloaded
                      ? 'Re-generate without charging'
                      : 'Generate the 3D file'
                  }
                >
                  {isGenerating && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${Math.max(
                          0,
                          Math.min(100, Number(progress) || 0)
                        )}%`,
                        backgroundColor: '#0ea5e9',
                        transition: 'width 0.4s ease',
                        zIndex: 0,
                      }}
                    />
                  )}

                  <span
                    style={{
                      position: 'relative',
                      zIndex: 2,
                      fontSize: '2vh',
                    }}
                  >
                    {isGenerating
                      ? `Creating… ${Math.max(
                          0,
                          Math.min(100, Number(progress) || 0)
                        )}%`
                      : 'Save Project & Create 3D File'}
                  </span>
                </button>
              </div>
              <div
                ref={viewerSectionRef}
                style={{
                  marginTop: '1.2vh',
                  width: '79vmin',
                  height: '79vmin',
                  border: '2px dashed #94a3b8',
                  borderRadius: '10px',
                  backgroundColor: '#f8fafc',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Sliding Extrusion Controls overlay (like Advanced Color Controls) */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    zIndex: 30,
                    background:
                      'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
                    color: 'white',
                    transform: showExtrusionOverlayPanel
                      ? 'translateY(0%)'
                      : 'translateY(calc(-100% + 2.8vh))',
                    transition: 'transform 0.25s ease-in-out',
                    willChange: 'transform',
                    fontSize: '0.8rem',
                    pointerEvents: 'auto',
                  }}
                >
                  {/* Handle */}
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowExtrusionOverlayPanel((prev) => !prev);
                    }}
                    style={{
                      height: '2.8vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderBottom: '1px solid rgba(255,255,255,0.4)',
                      backgroundColor: 'rgba(112,112,112,0.4)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: '2.5vh',
                        fontWeight: 600,
                        letterSpacing: '0.03em',
                      }}
                    >
                      {showExtrusionOverlayPanel
                        ? '▲ Extrusion Settings ▲'
                        : '▼ Extrusion Settings ▼'}
                    </span>
                  </div>

                  {/* CONTENT: Color + Extrusion controls (overlay clone) */}
                  <div
                    style={{
                      padding: showExtrusionOverlayPanel
                        ? '4px 8px 6px'
                        : '0 8px 0',
                      maxHeight: showExtrusionOverlayPanel ? '65%' : 0,
                      overflowY: 'auto', // allow vertical scroll
                      backgroundColor: 'rgba(112,112,112,0.5)',
                      fontSize: '1.8vh',
                      opacity: showExtrusionOverlayPanel ? 1 : 0,
                      transition:
                        'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                    }}
                  >
                    {Array.isArray(colorsForUi) &&
                      colorsForUi.length > 0 &&
                      Array.isArray(extrusionValues) && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.2vh',
                            width: '100%',
                          }}
                        >
                          {/* Headers */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              paddingBottom: '2px',
                            }}
                          >
                            {/* Colors header column (left) */}
                            <div
                              style={{
                                flexBasis: '50%',
                                maxWidth: '50%',
                                paddingRight: '0.4vw',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  fontSize: '1.6vh',
                                }}
                              >
                                Colors
                              </span>
                            </div>

                            {/* Layer Height header column (right) */}
                            <div
                              style={{
                                flexBasis: '50%',
                                maxWidth: '50%',
                                paddingLeft: '0.4vw',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                              }}
                            >
                              {/* This block mirrors the controls row layout: marginLeft + gap + widths */}
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5vh',
                                  marginLeft: '0.6vw', // <-- same as controls rows
                                }}
                              >
                                {/* left spacer = minus button slot */}
                                <div
                                  style={{
                                    width: '2.6vh',
                                    height: '2.6vh',
                                  }}
                                />

                                {/* center = input width, header centered */}
                                <div
                                  style={{
                                    width: '4.6vw', // same as input width
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 'bold',
                                      fontSize: '1.6vh',
                                      textAlign: 'center',
                                      width: '100%',
                                    }}
                                  >
                                    Layer Height
                                  </span>
                                </div>

                                {/* right spacer = plus button slot */}
                                <div
                                  style={{
                                    width: '2.6vh',
                                    height: '2.6vh',
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Background row (overlay) */}
                          <div
                            key="background-overlay"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginTop: '0.1vh',
                            }}
                          >
                            {/* Color name column */}
                            <div
                              style={{
                                flexBasis: '50%',
                                maxWidth: '50%',
                                paddingRight: '0.5vw',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <span style={{ fontSize: '1.6vh' }}>
                                {colorsForUi?.[0] || 'Background'}
                              </span>
                            </div>

                            {/* Controls column */}
                            <div
                              style={{
                                flexBasis: '50%',
                                maxWidth: '50%',
                                paddingLeft: '0.5vw',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: '0.5vh',
                              }}
                            >
                              {/* - button (disabled for background) */}
                              <button
                                type="button"
                                disabled
                                style={{
                                  width: '2.6vh',
                                  height: '2.6vh',
                                  borderRadius: '50%',
                                  border:
                                    '1px solid rgba(148,163,184,0.9)',
                                  backgroundColor:
                                    'rgba(148,163,184,0.45)',
                                  color: '#e5e7eb',
                                  fontSize: '1.8vh',
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'not-allowed',
                                  padding: 0,
                                }}
                              >
                                −
                              </button>

                              {/* VALUE (text input) */}
                              <input
                                type="text"
                                value={extrusionValues[0] ?? 1}
                                disabled
                                className="extrude-box"
                                style={{
                                  width: '4.6vw',
                                  fontSize: '1.4vh',
                                  padding: '0.4vh',
                                  textAlign: 'center',
                                  backgroundColor: '#e5e7eb',
                                  color: '#6b7280',
                                  border:
                                    '1px solid rgba(148,163,184,0.9)',
                                  borderRadius: '0.9vh',
                                  cursor: 'not-allowed',
                                }}
                              />

                              {/* + button (disabled for background) */}
                              <button
                                type="button"
                                disabled
                                style={{
                                  width: '2.6vh',
                                  height: '2.6vh',
                                  borderRadius: '50%',
                                  border:
                                    '1px solid rgba(148,163,184,0.9)',
                                  backgroundColor:
                                    'rgba(148,163,184,0.45)',
                                  color: '#e5e7eb',
                                  fontSize: '1.8vh',
                                  lineHeight: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'not-allowed',
                                  padding: 0,
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Remaining colors (overlay) */}
                          {colorsForUi.slice(1).map((color, i) => (
                            <div
                              key={`overlay-color-${i + 1}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginTop: '0.1vh',
                              }}
                            >
                              {/* Color name column */}
                              <div
                                style={{
                                  flexBasis: '50%',
                                  maxWidth: '50%',
                                  paddingRight: '0.5vw',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <span style={{ fontSize: '1.6vh' }}>
                                  {color}
                                </span>
                              </div>

                              {/* Controls column */}
                              <div
                                style={{
                                  flexBasis: '50%',
                                  maxWidth: '50%',
                                  paddingLeft: '0.5vw',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: '0.5vh',
                                }}
                              >
                                {/* - button */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    !controlsLocked &&
                                    stepExtrusion(i + 1, -1)
                                  }
                                  disabled={controlsLocked}
                                  style={{
                                    width: '2.6vh',
                                    height: '2.6vh',
                                    borderRadius: '50%',
                                    border:
                                      '1px solid rgba(148,163,184,0.9)',
                                    backgroundColor: controlsLocked
                                      ? 'rgba(148,163,184,0.45)'
                                      : '#0f172a',
                                    color: '#e5e7eb',
                                    fontSize: '1.8vh',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: controlsLocked
                                      ? 'not-allowed'
                                      : 'pointer',
                                    padding: 0,
                                    boxShadow: controlsLocked
                                      ? 'none'
                                      : '0 0 0 1px rgba(15,23,42,0.8)',
                                  }}
                                >
                                  −
                                </button>

                                {/* value input (no spinners) */}
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={extrusionValues[i + 1] ?? 1}
                                  onChange={(e) =>
                                    handleExtrusionChange(
                                      i + 1,
                                      e.target.value
                                    )
                                  }
                                  onBlur={() =>
                                    handleExtrusionBlur(i + 1)
                                  }
                                  onWheel={(e) =>
                                    e.currentTarget.blur()
                                  }
                                  disabled={controlsLocked}
                                  className="extrude-box"
                                  style={{
                                    width: '4.6vw',
                                    fontSize: '1.4vh',
                                    padding: '0.4vh',
                                    textAlign: 'center',
                                    backgroundColor: controlsLocked
                                      ? '#e5e7eb'
                                      : 'rgba(15,23,42,0.9)',
                                    color: '#e5e7eb',
                                    border:
                                      '1px solid rgba(148,163,184,0.9)',
                                    borderRadius: '0.9vh',
                                    cursor: controlsLocked
                                      ? 'not-allowed'
                                      : 'text',
                                    outline: 'none',
                                  }}
                                />

                                {/* + button */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    !controlsLocked &&
                                    stepExtrusion(i + 1, +1)
                                  }
                                  disabled={controlsLocked}
                                  style={{
                                    width: '2.6vh',
                                    height: '2.6vh',
                                    borderRadius: '50%',
                                    border:
                                      '1px solid rgba(148,163,184,0.9)',
                                    backgroundColor: controlsLocked
                                      ? 'rgba(148,163,184,0.45)'
                                      : '#0f172a',
                                    color: '#e5e7eb',
                                    fontSize: '1.8vh',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: controlsLocked
                                      ? 'not-allowed'
                                      : 'pointer',
                                    padding: 0,
                                    boxShadow: controlsLocked
                                      ? 'none'
                                      : '0 0 0 1px rgba(15,23,42,0.8)',
                                  }}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                          ))}

                            {/* Make it Flat toggle (overlay) — always ON and disabled */}
                            <div
                              style={{
                                marginTop: '1vh',
                                display: 'flex',
                                alignItems: 'center',
                                opacity: 0.6, // visually indicate locked
                              }}
                            >
                              <div
                                style={{
                                  position: 'relative',
                                  display: 'inline-block',
                                  marginRight: '0.6vh',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  id="flatCheckboxOverlay"
                                  checked={true}            // always checked
                                  disabled={true}           // fully disabled
                                  style={{
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    width: '2.4vh',
                                    height: '2.4vh',
                                    borderRadius: '0.5vh',
                                    border: '0.25vh solid rgba(255,255,255,0.8)',
                                    backgroundColor: 'rgba(15,23,42,0.85)', // always "checked" state color
                                    display: 'inline-block',
                                    cursor: 'not-allowed',
                                  }}
                                />
                                <span
                                  style={{
                                    position: 'absolute',
                                    left: '50%',
                                    top: '50%',
                                    transform: 'translate(-50%, -58%)',
                                    fontSize: '1.6vh',
                                    color: '#e5e7eb',
                                    pointerEvents: 'none',
                                  }}
                                >
                                  ✓
                                </span>
                              </div>

                              <label
                                htmlFor="flatCheckboxOverlay"
                                style={{
                                  fontSize: '1.6vh',
                                  userSelect: 'none',
                                  cursor: 'not-allowed',
                                }}
                              >
                                Make it Flat
                              </label>
                            </div>

                        </div>
                      )}
                  </div>
                </div>

                {/* Viewer / 2D preview content underneath */}
                {hasGenerated && emsPartUrls.length > 0 ? (
                  <>
                    {/* Viewer itself; lock controls while snapshot pending */}
                    <Viewer
                      urls={emsPartUrls}
                      colors={emsPartColors}
                      locked={!snapshotDone && pendingAutoSnapshot}
                      onReady={(renderer) => {
                        viewerRendererRef.current = renderer;
                      }}
                      onReadyCaptureApi={(api) => {
                        captureApiRef.current = api;
                      }}
                    />
                    {/* Click-blocking overlay until snapshot completes */}
                    {!snapshotDone && pendingAutoSnapshot && (
                      <div
                        style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'rgba(248,250,252,0.35)',
                          backdropFilter: 'blur(1px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 5,
                          pointerEvents: 'auto',
                          cursor: 'wait',
                        }}
                        title="Preparing preview…"
                      >
                        <div
                          style={{
                            padding: '10px 14px',
                            borderRadius: '10px',
                            background: 'rgba(241,245,249,0.9)',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.95vw',
                            color: '#334155',
                          }}
                        >
                          Preparing Rendering…
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {processedImage && (
                      <img
                        src={processedImage}
                        alt="Processed"
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          top: 0,
                          left: 0,
                          zIndex: 1,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                    {overlayImage && (
                      <img
                        src={overlayImage}
                        alt="Overlay"
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          top: 0,
                          left: 0,
                          zIndex: 2,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </>
                )}
              </div>

              {/* BOTTOM: Download button (appears after generation) */}
              <div
                style={{
                  marginTop: '1.5vh',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {showTopDownloadButton && (
                  <div
                    ref={topDownloadButtonRef}
                    style={{
                      position: 'relative',
                      cursor:
                        credits > 0 || zeroCreditsGrace
                          ? 'pointer'
                          : 'not-allowed',
                      opacity:
                        credits > 0 || zeroCreditsGrace ? 1 : 0.6,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        // Only trigger download when fully ready
                        if (
                          isDownloadEnabled &&
                          snapshotDone &&
                          !(credits <= 0 && !zeroCreditsGrace) &&
                          !isDownloaded
                        ) {
                          handleDownloadClick();
                        }
                      }}
                      disabled={
                        !isDownloadEnabled ||
                        !snapshotDone ||
                        isDownloaded ||
                        (credits <= 0 && !zeroCreditsGrace)
                      }
                      style={{
                        width: '80vmin',
                        padding: '0.8vmin 0',
                        background:
                          !isDownloadEnabled || !snapshotDone
                            ? '#cbd5e1'
                            : '#93c5fd',
                        border: '0px solid #94a3b8',
                        borderRadius: '10px',
                        fontSize: '2vh',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        cursor:
                          !isDownloadEnabled ||
                          !snapshotDone ||
                          isDownloaded ||
                          (credits <= 0 && !zeroCreditsGrace)
                            ? 'not-allowed'
                            : 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                      title={
                        (!isDownloadEnabled || !snapshotDone) &&
                        '3D rendering is still being prepared…'
                          ? '3D rendering is still being prepared…'
                          : (credits <= 0 && !zeroCreditsGrace)
                          ? 'No credits available'
                          : isDownloaded
                          ? 'Already downloaded'
                          : 'Download the zipped 3D files'
                      }
                    >
                    <span className={downloadReady ? 'flashing-text' : ''}>
                      {isDownloaded
                        ? 'Download may take a few seconds to initiate.'
                        : !isDownloadEnabled || !snapshotDone
                        ? '▲ 3D rendering will appear shortly ▲'
                        : 'Download Zipped 3D Files'}
                    </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const boxStyle = () => ({
  width: '100%',
  height: '100%',
  border: '2px dashed #94a3b8',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
});
